import { useAtomValue, useSetAtom } from "jotai";
import { useState } from "react";
import { Lightbox } from "@/components/Lightbox";
import { cx } from "@/lib/cx";
import { atoms } from "@/state";

// Renders the filtered models table. The filter input lives in SearchView at
// the top of `/` — ModelList just reads `atoms.models.filtered`. Clicking a
// row (anywhere except the thumbnail) opens the detail sheet; clicking the
// thumbnail opens a centered lightbox.
export const ModelList: React.FC = () => {
  const rows = useAtomValue(atoms.models.filtered);
  const openSheet = useSetAtom(atoms.models.openSheet);
  const [lightbox, setLightbox] = useState<{ url: string; alt: string } | null>(null);

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
      <div className="min-h-0 flex-1 overflow-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 border-b border-gray-200 bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500 shadow-[0_1px_0_rgb(229_231_235)] dark:border-gray-800 dark:bg-gray-950 dark:text-gray-400 dark:shadow-[0_1px_0_rgb(31_41_55)]">
            <tr>
              <th className="w-20 px-4 py-2 font-medium">Photo</th>
              <th className="px-4 py-2 font-medium">Model</th>
              <th className="px-4 py-2 font-medium">Series</th>
              <th className="px-4 py-2 font-medium">Description</th>
              <th className="px-4 py-2 font-medium">Option code status</th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 200).map((m) => (
              // biome-ignore lint/a11y/useSemanticElements: a <button> can't replace a table row; the whole <tr> is the click target
              <tr
                key={m.model}
                onClick={() => openSheet(m.model)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openSheet(m.model);
                  }
                }}
                tabIndex={0}
                role="button"
                aria-label={`Open ${m.model}`}
                className="cursor-pointer border-b border-gray-100 last:border-0 hover:bg-blue-50/40 focus:bg-blue-50/60 focus:outline-none dark:border-gray-800 dark:hover:bg-blue-950/30 dark:focus:bg-blue-950/40"
              >
                <td className="px-4 py-2">
                  {m.photo_url ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setLightbox({ url: m.photo_url, alt: m.description });
                      }}
                      aria-label={`View full photo for ${m.model}`}
                      className="block h-9 w-9 overflow-hidden rounded border border-gray-200 bg-white transition-transform hover:scale-110 dark:border-gray-700 dark:bg-gray-800"
                    >
                      <img
                        src={m.photo_url}
                        alt=""
                        loading="lazy"
                        className="h-full w-full object-contain"
                      />
                    </button>
                  ) : (
                    <div className="h-9 w-9 rounded border border-dashed border-gray-200 dark:border-gray-700" />
                  )}
                </td>
                <td className="px-4 py-2 font-mono font-medium text-blue-600 dark:text-blue-400">
                  {m.model}
                </td>
                <td className="px-4 py-2 text-xs text-gray-600 dark:text-gray-400">{m.series}</td>
                <td className="px-4 py-2 text-gray-800 dark:text-gray-200">{m.description}</td>
                <td className="px-4 py-2">
                  <span
                    className={cx(
                      "inline-block rounded px-2 py-0.5 text-xs font-medium",
                      m.hasTemplate
                        ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"
                        : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
                    )}
                  >
                    {m.hasTemplate ? "Complete" : "Basic"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > 200 && (
        <div className="border-t border-gray-100 px-4 py-2 text-xs text-gray-500 dark:border-gray-800 dark:text-gray-400">
          Showing first 200 of {rows.length.toLocaleString()} — refine the search to see more.
        </div>
      )}

      <Lightbox
        url={lightbox?.url ?? null}
        alt={lightbox?.alt}
        isOpen={lightbox !== null}
        onOpenChange={(open) => {
          if (!open) {
            setLightbox(null);
          }
        }}
      />
    </div>
  );
};
